import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, FolderOpen, Lightbulb } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  TAG_SOURCE_LABELS,
  useAssignMessage,
  useEopAssignableProjects,
  useMarkGeneral,
  type EopMessageDetail,
} from "@/features/eop_mail/api/mailbox";
import { extractError } from "./sanitize";

/**
 * Przypisanie wiadomości do przedsięwzięcia.
 *
 * Automat przypina sam (decyzja z 16.08.2026); pasek pokazuje, KTÓRA warstwa to
 * zrobiła (szara plakietka = robot, zielona = człowiek) i pozwala jednym ruchem
 * wskazać inne przedsięwzięcie — to wskazanie jest nadrzędne i rozlewa się na
 * automatycznie otagowane wiadomości wątku. Lista wyboru zawiera wyłącznie
 * przedsięwzięcia ENERGA (backend `/eop-mail/projects/`), bo przypięcie
 * korespondencji operatorskiej do przypadkowego projektu jest błędem, który
 * najszybciej podważa zaufanie do skrzynki.
 */
export function AssignBar({ message }: { message: EopMessageDetail }) {
  const { data: projects } = useEopAssignableProjects();
  const assign = useAssignMessage();
  const markGeneral = useMarkGeneral();
  const [picked, setPicked] = useState("");
  const [error, setError] = useState("");

  const run = (project: string) => {
    setError("");
    assign.mutate(
      { id: message.id, project },
      { onError: (err) => setError(extractError(err)) },
    );
  };

  const options = (projects ?? []).map((p) => ({
    value: p.id,
    label: `${p.short_name} — ${p.name}${p.is_mine ? " (moje)" : ""}`,
  }));

  if (message.is_general) {
    return (
      <div className="flex flex-wrap items-center gap-2 border border-[var(--color-border)] px-3 py-2">
        <Badge color="gray">korespondencja ogólna</Badge>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          Nie dotyczy jednego przedsięwzięcia — poza kolejką nierozpoznanych.
        </span>
      </div>
    );
  }

  if (message.project) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <FolderOpen size={13} className="shrink-0 text-[var(--color-text-muted)]" />
        <Link
          to={`/power-projects/${message.project.id}?tab=eopmail`}
          className="inline-flex shrink-0 items-center gap-0.5 font-mono text-[12px] font-bold text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
          title="Przejdź do przedsięwzięcia"
        >
          {message.project.short_name}
          <ArrowUpRight size={11} />
        </Link>
        <span
          className="min-w-0 truncate text-[12px] text-[var(--color-text-muted)]"
          title={message.project.name}
        >
          {message.project.name}
        </span>
        {message.tag_source && (
          <Badge
            color={message.tag_source === "manual" ? "green" : "gray"}
            className="shrink-0 whitespace-nowrap"
          >
            {TAG_SOURCE_LABELS[message.tag_source]}
          </Badge>
        )}
        <div className="ml-auto shrink-0">
          <SearchableSelect
            variant="ghost"
            options={options}
            placeholder="zmień…"
            value={picked}
            onChange={(e) => {
              setPicked(e.target.value);
              if (e.target.value) run(e.target.value);
            }}
          />
        </div>
        {error && (
          <p className="w-full text-[11px] text-[var(--color-danger)]">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 border border-[var(--color-border)] px-3 py-2 shadow-[inset_3px_0_0_var(--color-primary)]">
      <div className="flex items-center gap-2">
        <Badge color="orange">nierozpoznane</Badge>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          Nieprzypięta do przedsięwzięcia.
        </span>
      </div>

      {message.suggested_project && (
        <div className="flex flex-wrap items-center gap-2">
          <Lightbulb size={14} className="text-[var(--color-primary)]" />
          <span className="text-[13px] text-[var(--color-text-primary)]">
            {message.suggested_project.short_name} — {message.suggested_project.name}
          </span>
          {message.suggestion_reason && (
            <span className="text-[11px] text-[var(--color-text-muted)]">
              {message.suggestion_reason}
            </span>
          )}
          <Button
            size="sm"
            onClick={() => run(message.suggested_project!.id)}
            disabled={assign.isPending}
          >
            <Check size={13} className="mr-1" />
            Przypnij
          </Button>
        </div>
      )}

      {/* Propozycja AI: to w ogóle nie jest sprawa projektowa. Z powodem
          i przyciskiem — jak każda propozycja, czeka na człowieka. */}
      {message.suggested_general && (
        <div className="flex flex-wrap items-center gap-2">
          <Lightbulb size={14} className="text-[var(--color-primary)]" />
          <span className="text-[13px] text-[var(--color-text-primary)]">
            AI: nie dotyczy jednego przedsięwzięcia
          </span>
          {message.suggestion_reason && (
            <span className="text-[11px] text-[var(--color-text-muted)]">
              {message.suggestion_reason}
            </span>
          )}
          <Button
            size="sm"
            onClick={() =>
              markGeneral.mutate(message.id, {
                onError: (err) => setError(extractError(err)),
              })
            }
            disabled={markGeneral.isPending}
          >
            <Check size={13} className="mr-1" />
            Potwierdź
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[16rem] flex-1">
          <SearchableSelect
            options={options}
            placeholder={
              message.suggested_project
                ? "…albo wybierz inne przedsięwzięcie"
                : "wybierz przedsięwzięcie"
            }
            value={picked}
            onChange={(e) => {
              setPicked(e.target.value);
              if (e.target.value) run(e.target.value);
            }}
          />
        </div>
        {!message.suggested_general && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              markGeneral.mutate(message.id, {
                onError: (err) => setError(extractError(err)),
              })
            }
          >
            To nie jest sprawa projektowa
          </Button>
        )}
      </div>
      {error && <p className="text-[11px] text-[var(--color-danger)]">{error}</p>}
    </div>
  );
}
